import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs, { Dayjs } from 'dayjs';

interface CalendarEvent {
  id: number;
  name: string;
  date: Date;
  daysRemaining: number;
  type: 'visa' | 'trial';
  additionalInfo?: string;
}

interface MonthlyEventsCalendarProps {
  events: CalendarEvent[];
  title: string;
}

function MonthlyEventsCalendar({ events, title }: MonthlyEventsCalendarProps) {
  const theme = useTheme();
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());

  const getDayLetter = (day: Dayjs): string => {
    const dayNumber = day.day(); 
    const frenchDays = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
    return frenchDays[dayNumber];
  };

  // Grouper les événements par date
  const eventsByDate = events.reduce((acc, event) => {
    const dateKey = dayjs(event.date).format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);

  // Déterminer la couleur selon l'urgence
  const getEventColor = (daysRemaining: number) => {
    if (daysRemaining <= 15) {
      return theme.palette.error.main;
    } else if (daysRemaining <= 30) {
      return theme.palette.warning.main;
    }
    return theme.palette.success.main;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const isToday = (day: Dayjs) => day.isSame(dayjs(), 'day');

  const daysInMonth = currentMonth.daysInMonth();
  const monthDays: Dayjs[] = [];
  for (let i = 1; i <= daysInMonth; i++) {
    monthDays.push(currentMonth.date(i));
  }

  return (
    <Paper 
      elevation={0} 
      variant="outlined"
      sx={{ p: 1 }}
    >

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: "secondary.contrastText", fontSize: '0.85rem' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={handlePreviousMonth} sx={{ padding: 0.5 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography 
            variant="body2" 
            sx={{ 
              minWidth: 120, 
              textAlign: 'center',
              fontWeight: 'bold',
              color: "primary.main",
              fontSize: '0.8rem'
            }}
          >
            {currentMonth.format('MMMM YYYY')}
          </Typography>
          <IconButton size="small" onClick={handleNextMonth} sx={{ padding: 0.5 }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', borderBottom: `1px solid ${theme.palette.divider}`, pb: 0.25, mb: 0.5 }}>
          {monthDays.map((day) => {
            const today = isToday(day);
            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  flex: 1,
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: today ? 'bold' : 'normal',
                    color: today ? theme.palette.primary.main : theme.palette.text.secondary,
                    fontSize: '0.5rem',
                  }}
                >
                  {getDayLetter(day)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: today ? 'bold' : 'normal',
                    color: today ? theme.palette.primary.main : theme.palette.text.primary,
                    fontSize: '0.65rem',
                  }}
                >
                  {day.date()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Events timeline */}
        <Box sx={{ position: 'relative', minHeight: 30, display: 'flex' }}>
          {monthDays.map((day, dayIndex) => {
            const dateKey = day.format('YYYY-MM-DD');
            const dayEvents = eventsByDate[dateKey] || [];
            const today = isToday(day);

            return (
              <Box
                key={dateKey}
                sx={{
                  flex: 1,
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 0.25,
                  pt: 0.5,
                  px: 0.25,
                  backgroundColor: today ? `${theme.palette.primary.main}05` : 'transparent',
                  alignItems: 'start',
                  justifyItems: 'center',
                  '&::before': today ? {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '1.5px',
                    backgroundColor: theme.palette.primary.main,
                    opacity: 0.4,
                    transform: 'translateX(-50%)',
                  } : {},
                }}
              >
                {dayEvents.map((event, eventIndex) => {
                  // Recalculer les jours restants pour être sûr d'avoir la bonne valeur
                  const actualDaysRemaining = dayjs(event.date).startOf('day').diff(dayjs().startOf('day'), 'day');
                  const color = getEventColor(actualDaysRemaining);
                  return (
                    <Tooltip
                      key={event.id}     
                      title={
                        <div className='text-sm' style={{color: theme.palette.primary.contrastText}}>
                          <div style={{ fontWeight: 'bold' }}>{event.name}</div>
                          <div>{event.type === 'visa' ? ' Expiration visa' : ' Fin période essai'}</div>
                            {actualDaysRemaining > 0 
                              ? `dans ${actualDaysRemaining} jour${actualDaysRemaining > 1 ? 's' : ''}`
                              : actualDaysRemaining === 0
                                ? "aujourd'hui"
                                : `expiré depuis ${Math.abs(actualDaysRemaining)} jour${Math.abs(actualDaysRemaining) > 1 ? 's' : ''}`
                            }
                          {event.additionalInfo && (
                            <div>{event.additionalInfo}</div>
                          )}
                        </div>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: color,
                          cursor: 'pointer',
                          border: `1.5px solid ${color}`,
                          transition: 'all 0.2s',
                          zIndex: 1,
                          '&:hover': {
                            width: 12,
                            height: 12,
                            border: `2px solid ${color}`,
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}

export default MonthlyEventsCalendar;
